import { Copy, Scissors, CopyPlus, ClipboardPaste, Trash2, UserRoundX } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Green (brand `primary`) hover/focus for schedule context-menu items. The
 * shadcn default highlights with `accent`, which is pink in this theme — the
 * crew schedule menus use the brand green instead. Exported so the inline
 * "Paste here" row menus stay visually consistent.
 */
export const ITEM_FOCUS = "focus:bg-primary focus:text-primary-foreground";

interface AssignmentContextMenuProps {
  /** The assignment block element — a single DOM node the menu/tooltip attach to. */
  children: React.ReactElement;
  /** Tooltip body shown on hover. */
  tooltip: React.ReactNode;
  /** Pass `false` to force the tooltip closed (e.g. while resizing); otherwise leave undefined. */
  tooltipOpen?: boolean;
  onCopy: () => void;
  onCut: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onPaste: () => void;
  /** Whether the clipboard currently holds something to paste. */
  canPaste: boolean;
  /** When true, show a per-slot assist toggle (only for source rows whose
   *  crew member has a dedicated assistant). */
  assistExcludable?: boolean;
  /** Current opt-out state of this slot's mirroring. */
  assistExcluded?: boolean;
  /** Toggle whether this slot is mirrored to the assistant. */
  onToggleAssistExclusion?: () => void;
}

/**
 * Wraps an assignment block with a right-click menu (Copy / Cut / Duplicate /
 * Paste / Delete) while preserving the existing hover tooltip.
 *
 * Both the ContextMenu and Tooltip target the same DOM node via nested
 * `asChild` Slots — the documented Radix pattern for combining the two on one
 * element. Shared between the artist-row and custom-row block renders so the
 * menu stays identical in both places (DRY / single source of menu truth).
 */
export function AssignmentContextMenu({
  children,
  tooltip,
  tooltipOpen,
  onCopy,
  onCut,
  onDuplicate,
  onDelete,
  onPaste,
  canPaste,
  assistExcludable,
  assistExcluded,
  onToggleAssistExclusion,
}: AssignmentContextMenuProps) {
  return (
    <Tooltip open={tooltipOpen}>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <TooltipTrigger asChild>{children}</TooltipTrigger>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-44">
          <ContextMenuItem className={ITEM_FOCUS} onSelect={onCopy}>
            <Copy className="mr-2 h-4 w-4" />
            Copy
            <ContextMenuShortcut>⌘C</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem className={ITEM_FOCUS} onSelect={onCut}>
            <Scissors className="mr-2 h-4 w-4" />
            Cut
            <ContextMenuShortcut>⌘X</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem className={ITEM_FOCUS} onSelect={onDuplicate}>
            <CopyPlus className="mr-2 h-4 w-4" />
            Duplicate
          </ContextMenuItem>
          <ContextMenuItem className={ITEM_FOCUS} disabled={!canPaste} onSelect={onPaste}>
            <ClipboardPaste className="mr-2 h-4 w-4" />
            Paste
            <ContextMenuShortcut>⌘V</ContextMenuShortcut>
          </ContextMenuItem>
          {assistExcludable && onToggleAssistExclusion && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem className={ITEM_FOCUS} onSelect={onToggleAssistExclusion}>
                <UserRoundX className="mr-2 h-4 w-4" />
                {assistExcluded ? "Restore assist for this slot" : "Remove assist for this slot"}
              </ContextMenuItem>
            </>
          )}
          <ContextMenuSeparator />
          <ContextMenuItem className={`text-destructive ${ITEM_FOCUS}`} onSelect={onDelete}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
